import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { ArrowRight, Tag, ShoppingBag } from "lucide-react";
import shopProducts, { PAYPAL_EMAIL, PAYPAL_MERCHANT_ID } from "../data/shopProducts";

const FILTERS = ["All", "Merch", "Music", "Digital"];

// Build a PayPal "Buy Now" cart URL from a hosted button id
const buildHostedButtonUrl = (id) =>
  `https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=${encodeURIComponent(id)}`;

// Fallback: build a direct PayPal Buy-Now URL using the merchant ID + product info
const buildMerchantBuyUrl = (productTitle, price, sku) => {
  const params = new URLSearchParams({
    cmd: "_xclick",
    business: PAYPAL_MERCHANT_ID,
    item_name: productTitle,
    item_number: sku,
    amount: Number(price).toFixed(2),
    currency_code: "USD",
    no_shipping: "0",
  });
  return `https://www.paypal.com/cgi-bin/webscr?${params.toString()}`;
};

const getCheckoutUrl = (p) => {
  if (p.comingSoon) return null;
  if (p.paypalButtonId) return buildHostedButtonUrl(p.paypalButtonId);
  if (p.paypalMeUrl) return p.paypalMeUrl;
  if (p.price) return buildMerchantBuyUrl(p.title, p.price, p.id);
  return null;
};

const formatPrice = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

export default function Shop() {
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered =
    filter === "All"
      ? shopProducts
      : shopProducts.filter((p) => p.category === filter);

  return (
    <div data-testid="shop-page" className="min-h-screen bg-white text-black">
      <Navigation />

      <section className="pt-28 md:pt-36 pb-20 md:pb-28 px-6 md:px-12 lg:px-20 border-b-2 border-black">
        <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-black/60 mb-6">
          [ 00 / The Shop ]
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <h1 className="lg:col-span-8 font-display uppercase leading-[0.82] tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]">
            Wear<br />the Sound.
          </h1>
          <p className="lg:col-span-4 text-black/70 max-w-md text-base md:text-lg">
            A curated store for merch, music and digital tools built in-house at Infinagon. Secure checkout via PayPal - new drops roll out quarterly.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <h2 className="font-display uppercase tracking-tighter text-5xl md:text-6xl">
            Products
          </h2>
          <div className="flex gap-0 border-2 border-black">
            {FILTERS.map((f, i) => (
              <button
                key={f}
                data-testid={`shop-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-3 font-mono text-[11px] tracking-[0.25em] uppercase ${
                  i > 0 ? "border-l-2 border-black" : ""
                } ${filter === f ? "bg-black text-white" : "bg-white text-black hover:bg-black/5"}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-2 border-black">
          {filtered.map((p, i) => {
            const url = getCheckoutUrl(p);
            return (
              <article
                key={p.id}
                data-testid={`shop-product-${p.id}`}
                className={`group relative bg-white flex flex-col ${
                  i > 0 ? "md:border-l-2 border-black border-t-2 md:border-t-0" : ""
                }`}
              >
                <div className="aspect-[4/5] overflow-hidden border-b-2 border-black relative bg-[hsl(0,0%,96%)]">
                  <img
                    src={p.image}
                    alt={p.title}
                    className={`w-full h-full object-cover ${
                      p.category === "Music" ? "" : "grayscale"
                    } group-hover:scale-105 transition-transform duration-700`}
                  />
                  <span className="absolute top-4 left-4 bg-white border-2 border-black px-3 py-1 font-mono text-[10px] tracking-[0.25em] uppercase flex items-center gap-2">
                    <Tag size={12} />
                    {p.category}
                  </span>
                  {p.soldOut && (
                    <span className="absolute top-4 right-4 bg-black text-white px-3 py-1 font-mono text-[10px] tracking-[0.25em] uppercase">
                      Sold Out
                    </span>
                  )}
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-1">
                  <h3 className="font-display text-3xl uppercase tracking-tighter leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-black/70 flex-1">{p.description}</p>
                  <div className="mt-8 flex items-center justify-between gap-4">
                    <span className="font-mono text-lg tracking-[0.1em]">
                      {formatPrice(p.price)}
                    </span>
                    {url && !p.soldOut ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`shop-buy-${p.id}`}
                        className="font-mono text-[11px] tracking-[0.25em] uppercase bg-black text-white px-5 py-3 border-2 border-black hover:bg-white hover:text-black transition-colors flex items-center gap-2"
                      >
                        <ShoppingBag size={14} />
                        Buy · PayPal
                      </a>
                    ) : (
                      <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-black/50 flex items-center gap-2">
                        Coming Soon
                        <ArrowRight size={14} />
                      </span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-10 font-mono text-[11px] tracking-[0.25em] uppercase text-black/50">
          · Checkout secured by PayPal ·
        </p>
        {/* NOTE TO OWNER (hidden from the rendered page):
            To add or edit shop items, see /src/data/shopProducts.js
            — each category has a copy/paste template inside it. */}
      </section>

      <Footer />
    </div>
  );
}
